import { useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import type { IconName } from '@/components/ui/Icon';
import { useI18n } from '@/hooks/useI18n';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import { useShell } from '@/hooks/useShell';
import { homeNavigateOptions } from '@/lib/navigationState';
import { routes } from '@/lib/routes';

const ICONS: Record<string, IconName> = {
  info: 'info',
  news: 'news',
  trophy: 'trophy',
  document: 'document',
  graduation: 'graduation',
  briefcase: 'briefcase',
  book: 'document',
  chat: 'chat',
};

export const MegaNav = () => {
  const { t, locale } = useI18n();
  const { shell } = useShell();
  const navigate = useNavigate();
  const location = useLocation();
  const [openId, setOpenId] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(navRef, () => setOpenId(null), openId !== null);

  const sections = shell?.navigation ?? [];
  const activeSection = location.pathname.startsWith('/portal/')
    ? location.pathname.split('/')[2]
    : null;

  return (
    <div className="nav-shell portal-nav-shell" ref={navRef}>
      <nav className="mainnav portal-mainnav" aria-label={t('nav.home')} data-element="B01-E04.00">
        <button
          type="button"
          className={`home-btn portal-home-btn${location.pathname === routes.home ? ' is-nav-active' : ''}`}
          aria-label={t('nav.home')}
          onClick={() => {
            setOpenId(null);
            navigate(routes.home, homeNavigateOptions(location.pathname, location.state));
          }}
        >
          <Icon name="home" size={20} strokeWidth={2} />
        </button>

        <ul className="nav-root portal-nav-root">
          {sections.map((section) => {
            const isOpen = openId === section.id;
            const isActive = activeSection === section.id;
            const label = section.label[locale];
            const icon = ICONS[section.icon] ?? 'info';
            const sectionHref = section.href ?? routes.portalSection(section.id);

            if (section.children.length === 0) {
              return (
                <li
                  key={section.id}
                  className={isActive ? 'is-nav-active' : undefined}
                  data-element={section.element_id}
                  data-active={isActive ? 'true' : undefined}
                  style={{ '--accent': section.accent, '--portal-accent': section.accent } as CSSProperties}
                >
                  <button
                    type="button"
                    className="portal-nav-button"
                    onClick={() => {
                      setOpenId(null);
                      navigate(sectionHref);
                    }}
                  >
                    <span className="nav-icon portal-nav-icon">
                      <Icon name={icon} size={16} strokeWidth={1.9} />
                    </span>
                    {label}
                  </button>
                </li>
              );
            }

            return (
              <li
                key={section.id}
                className={`${isOpen ? 'open' : ''}${isActive ? ' is-nav-active' : ''}`.trim()}
                data-open={isOpen}
                data-active={isActive ? 'true' : undefined}
                data-element={section.element_id}
                style={{ '--accent': section.accent, '--portal-accent': section.accent } as CSSProperties}
                onMouseEnter={() => setOpenId(section.id)}
                onMouseLeave={() => setOpenId((current) => (current === section.id ? null : current))}
              >
                <button
                  type="button"
                  className="portal-nav-button"
                  aria-expanded={isOpen}
                  aria-haspopup="menu"
                  onClick={() => {
                    navigate(sectionHref);
                    setOpenId(isOpen ? null : section.id);
                  }}
                >
                  <span className="nav-icon portal-nav-icon">
                    <Icon name={icon} size={16} strokeWidth={1.9} />
                  </span>
                  {label}
                  <b className="caret" aria-hidden="true">
                    ⌄
                  </b>
                </button>
                <div className="drop portal-drop" role="menu">
                  {section.children.map((child) => (
                    <Link
                      key={child.id}
                      to={child.href}
                      role="menuitem"
                      onClick={() => setOpenId(null)}
                    >
                      {child.label[locale]}
                    </Link>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};
